// src/pages/Accounting/ChartOfAccounts/components/FilterBar.tsx
// Chart of accounts filter row: search by name/number · All ▾ · Export · Print · Customize · pagination.
// Only the search input is pill-shaped; everything else keeps its original rounded-lg look.

import React, { useEffect, useRef, useState } from "react";
import { Search, Download, Printer, SlidersHorizontal, ChevronDown } from "lucide-react";

interface FilterBarProps {
  query: string;
  onQueryChange: (q: string) => void;
  typeFilter: string;
  onTypeFilterChange: (t: string) => void;
  pageStart: number;
  pageEnd: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onExport: () => void;
  onPrint: () => void;
  onCustomize: () => void;
}

const TYPE_OPTIONS: Array<{ group: string; items: string[] }> = [
  { group: "", items: ["All"] },
  { group: "Assets", items: ["Asset"] },
  { group: "Liabilities", items: ["Liability"] },
  { group: "Equity", items: ["Equity"] },
  { group: "Income", items: ["Income"] },
  { group: "Expense", items: ["Expense"] },
];

export default function FilterBar(props: FilterBarProps) {
  const [typeOpen, setTypeOpen] = useState(false);
  const typeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!typeOpen) return;
    const close = (e: MouseEvent) => {
      if (typeRef.current && !typeRef.current.contains(e.target as Node)) setTypeOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [typeOpen]);

  const pickType = (t: string) => () => { props.onTypeFilterChange(t); setTypeOpen(false); };

  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
      {/* Search + All filter */}
      <div className="flex items-center gap-2 flex-1 md:max-w-[560px]">
        {/* Pill-shaped search */}
        <div className="relative flex-1 md:max-w-[340px]">
          <Search size={14} strokeWidth={2} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink4 pointer-events-none" />
          <input
            value={props.query}
            onChange={(e) => props.onQueryChange(e.target.value)}
            placeholder="Filter by name or number"
            className="w-full bg-white border border-line rounded-full px-10 py-2 text-[12.5px] text-ink placeholder:text-ink4 outline-none focus:border-teal-dark"
          />
        </div>

        <div ref={typeRef} className="relative">
          <button
            type="button"
            onClick={() => setTypeOpen((v) => !v)}
            className="inline-flex items-center justify-between gap-2 bg-white border border-line rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-ink cursor-pointer min-w-[120px] md:min-w-[160px]"
          >
            {props.typeFilter}
            <ChevronDown size={12} strokeWidth={2.5} className="text-ink3" />
          </button>
          {typeOpen && (
            <div className="absolute left-0 top-full mt-1.5 w-[220px] bg-white border border-line rounded-xl shadow-[0_10px_32px_rgba(14,26,26,0.12)] py-1.5 z-50 overflow-hidden max-h-[320px] overflow-y-auto">
              {TYPE_OPTIONS.map((grp) => (
                <div key={grp.group || "all"}>
                  {grp.group && (
                    <div className="px-3.5 pt-2 pb-1 text-[10.5px] text-ink3 font-bold uppercase tracking-wider">
                      {grp.group}
                    </div>
                  )}
                  {grp.items.map((item) => {
                    const isSel = props.typeFilter === item;
                    return (
                      <button
                        key={item}
                        type="button"
                        onClick={pickType(item)}
                        className={`w-full flex items-center justify-between px-3.5 py-2 text-left text-[12.5px] border-none bg-transparent cursor-pointer hover:bg-panel ${
                          isSel ? "text-teal-dark font-semibold" : "text-ink"
                        }`}
                      >
                        {item}
                        {isSel && <span className="text-teal-dark">✓</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Icon group: Export · Print · Customize + pagination */}
      <div className="flex items-center gap-1.5">
        <IconButton tooltip="Export" onClick={props.onExport}>
          <Download size={14} strokeWidth={2} />
        </IconButton>
        <IconButton tooltip="Print" onClick={props.onPrint}>
          <Printer size={14} strokeWidth={2} />
        </IconButton>
        <IconButton tooltip="Customize" onClick={props.onCustomize}>
          <SlidersHorizontal size={14} strokeWidth={2} />
        </IconButton>

        <div className="inline-flex items-center gap-2 text-[12px] text-ink3 ml-2">
          <button type="button" onClick={props.onPrev} className="px-1.5 py-0.5 text-ink4 bg-transparent border-none cursor-pointer hover:text-ink">‹ Previous</button>
          <strong className="text-ink2 tabular-nums">{props.pageStart} - {props.pageEnd}</strong>
          <button type="button" onClick={props.onNext} className="px-1.5 py-0.5 text-ink4 bg-transparent border-none cursor-pointer hover:text-ink">Next ›</button>
        </div>
      </div>
    </div>
  );
}

function IconButton({ tooltip, onClick, children }: { tooltip: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onClick}
        className="w-8 h-8 rounded-md bg-white border border-line text-ink3 inline-flex items-center justify-center cursor-pointer hover:bg-[#FAFBFB]"
        aria-label={tooltip}
      >
        {children}
      </button>
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-ink text-white text-[11px] font-semibold px-2 py-1 rounded-md whitespace-nowrap pointer-events-none z-50">
        {tooltip}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ink" />
      </span>
    </div>
  );
}